import { Calculator, CaseSensitive, Dices, Gavel, LucideIcon, Sheet, Timer, Zap } from 'lucide-react'
import { FlagName, isFeatureEnabled } from '../services/flagsService'

// Single source for the tools shown in the header "Alətlər" menu, the /tools hub
// and the home launcher row. Add a new tool here instead of placing it by hand.

export type ToolGroup = 'game' | 'organizer'

export const TOOL_TONES = {
  blue:   { chip: 'text-blue-600 dark:text-blue-300',     tile: 'bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-300 group-hover:bg-blue-600 group-hover:text-white',         card: 'border-blue-100 dark:border-blue-900 from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950',         link: 'text-blue-600 dark:text-indigo-300' },
  green:  { chip: 'text-green-600 dark:text-green-300',   tile: 'bg-green-100 dark:bg-green-900/60 text-green-600 dark:text-green-300 group-hover:bg-green-600 group-hover:text-white',     card: 'border-green-100 dark:border-green-900 from-green-50 to-teal-50 dark:from-green-950 dark:to-teal-950',     link: 'text-green-600 dark:text-green-300' },
  orange: { chip: 'text-orange-600 dark:text-orange-300', tile: 'bg-orange-100 dark:bg-orange-900/60 text-orange-600 dark:text-orange-300 group-hover:bg-orange-500 group-hover:text-white', card: 'border-orange-100 dark:border-orange-900 from-orange-50 to-yellow-50 dark:from-orange-950 dark:to-yellow-950', link: 'text-orange-600 dark:text-orange-300' },
  amber:  { chip: 'text-amber-600 dark:text-amber-300',   tile: 'bg-amber-100 dark:bg-amber-900/60 text-amber-600 dark:text-amber-300 group-hover:bg-amber-500 group-hover:text-white',     card: 'border-amber-100 dark:border-amber-900 from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950', link: 'text-amber-600 dark:text-amber-300' },
  violet: { chip: 'text-violet-600 dark:text-violet-300', tile: 'bg-violet-100 dark:bg-violet-900/60 text-violet-600 dark:text-violet-300 group-hover:bg-violet-600 group-hover:text-white', card: 'border-violet-100 dark:border-violet-900 from-violet-50 to-pink-50 dark:from-violet-950 dark:to-pink-950', link: 'text-violet-600 dark:text-violet-300' },
  rose:   { chip: 'text-rose-600 dark:text-rose-300',     tile: 'bg-rose-100 dark:bg-rose-900/60 text-rose-600 dark:text-rose-300 group-hover:bg-rose-600 group-hover:text-white',         card: 'border-rose-100 dark:border-rose-900 from-rose-50 to-fuchsia-50 dark:from-rose-950 dark:to-fuchsia-950',   link: 'text-rose-600 dark:text-rose-300' },
} as const

export interface Tool {
  id: string
  to: string
  title: string
  // Label for the compact home launcher chip
  short: string
  description: string
  icon: LucideIcon
  group: ToolGroup
  tone: keyof typeof TOOL_TONES
  // Gates the tool everywhere (its route redirects home when off)
  flag?: FlagName
  // Shows the tool in the home launcher row
  homeFlag?: FlagName
}

export const TOOL_GROUPS: { id: ToolGroup; title: string; description: string }[] = [
  { id: 'game',      title: 'Oyun',                description: 'Dostlarla və ya komanda ilə oynamaq üçün.' },
  { id: 'organizer', title: 'Turnir təşkilatçısı', description: 'Turnir keçirmək üçün lazım olan alətlər.' },
]

export const TOOLS: Tool[] = [
  {
    id: 'random-package',
    to: '/packages/random',
    title: 'Təsadüfi paket',
    short: 'Təsadüfi paket',
    description: '12–36 təsadüfi NHN sualından ibarət paket.',
    icon: Dices,
    group: 'game',
    tone: 'violet',
    flag: 'RANDOM_PACKAGE',
    homeFlag: 'RANDOM_PACKAGE',
  },
  {
    id: 'buzzer',
    to: '/buzzer',
    title: 'Düymə sistemi',
    short: 'Düymə sistemi',
    description: 'Xal sayğacı ilə düymə sistemi. Otaq yaradın / qoşulun.',
    icon: Zap,
    group: 'game',
    tone: 'green',
    homeFlag: 'HOME_BUZZER_CARD',
  },
  {
    id: 'scoring',
    to: '/scoring',
    title: 'Xal sayğacı',
    short: 'Xal sayğacı',
    description: 'Düymə sistemi ilə və ya ayrıca oyunçuların xallarını hesablayın.',
    icon: Calculator,
    group: 'game',
    tone: 'orange',
    flag: 'SCORING_BOARD',
    homeFlag: 'SCORING_BOARD',
  },
  {
    id: 'initials',
    to: '/initials',
    title: 'İnisiallar',
    short: 'İnisiallar',
    description: 'Seçilmiş hərflərə uyğun məşhur şəxsləri tapın. Taymer, hərf yoxlaması və rəqib təsdiqi ilə.',
    icon: CaseSensitive,
    group: 'game',
    tone: 'amber',
    flag: 'INITIALS_GAME',
  },
  {
    id: 'timer',
    to: '/timer',
    title: 'NHN Taymeri',
    short: 'Taymer',
    description: 'Sual, duplet və blits üçün tam ekran taymer. Səs siqnalları ilə.',
    icon: Timer,
    group: 'organizer',
    tone: 'blue',
    homeFlag: 'HOME_TIMER_CARD',
  },
  {
    id: 'sheets',
    to: '/sheets',
    title: 'Cədvəli izlə',
    short: 'Cədvəl',
    description: 'Nəticələr cədvəlini Google Sheets-dən canlı göstərin. Hər 3 saniyədən bir yenilənir.',
    icon: Sheet,
    group: 'organizer',
    tone: 'amber',
    flag: 'SHEET_VIEWER',
    homeFlag: 'HOME_SHEET_CARD',
  },
  {
    id: 'appeals',
    to: '/tournament-panel/appeals',
    title: 'Apelyasiya meneceri',
    short: 'Apelyasiya',
    description: 'Apelyasiya otağı yaradın. Komandalar apelyasiya yazır, jürilər rəy verir, siz hamısını izləyirsiniz.',
    icon: Gavel,
    group: 'organizer',
    tone: 'rose',
    flag: 'TOURNAMENT_PANEL',
  },
]

export const availableTools = () => TOOLS.filter(t => !t.flag || isFeatureEnabled(t.flag))

export const homeTools = () => availableTools().filter(t => t.homeFlag && isFeatureEnabled(t.homeFlag))

export const toolsInGroup = (group: ToolGroup) => availableTools().filter(t => t.group === group)
